// src/lib/leadWatchRules.js — the ONE definition of "may this client have a lead alert" and
// "why does this lead match, and what must the agent still ask the owner" (owner 2026-09-21).
//
// Two consumers, one text:
//   · the Desk (LeadAlertToggle / requirementToCommitted) imports it as a module;
//   · the Telegram notifier (server-lib-staging/notifier/sentavus-notifier.js, CommonJS, shipped as
//     ONE file) carries a GENERATED copy between `// <lead-watch-rules>` markers. Regenerate with
//     `node scripts/gen-notifier-lead-rules.mjs`; leadWatchRules.test.mjs fails when they drift.
//     So: plain functions, no imports, no JSX, no i18n — the notifier speaks Georgian only.
//
// ⚠️ Before this file the notifier sent EVERY row the leads search returned, with a one-line
// body: property type was never part of the search (a flat client heard about land), the Desk's
// pass / possible / miss classification never ran (a "possible" row read as a match), and a
// client with nothing but "rent" could be watched — 13,760 leads.

// requirement.property_category → the listing `kind` the leads search filters on. A category
// outside this map ("other", null) is NOT a type: no type filter is set.
export const LEAD_WATCH_CATEGORY_KIND = {
  apartment: 'apartment', room: 'room', house: 'house', cottage: 'house',
  land: 'land', commercial: 'commercial', office: 'commercial', garage: 'commercial',
}
// requirement.intent (demand vocabulary) → listing intent (supply vocabulary). Same bridge as
// deskFeed.js CLIENT_INTENT_OF, read the other way.
export const LEAD_WATCH_INTENT = {
  rent: 'rent', buy: 'sale', sale: 'sale', girao: 'girao', short_term_rent: 'daily_rent', daily_rent: 'daily_rent',
}
// Kinds a room count or floor area does not describe. Everything else — including an unknown
// type, which on live data is almost always a home — needs a size before it can be watched.
const NO_SIZE_KINDS = new Set(['land', 'commercial'])

const present = v => v != null && v !== ''
const numOf = v => (present(v) && Number.isFinite(Number(v)) ? Number(v) : null)
const arr = v => (Array.isArray(v) ? v.filter(present) : [])

// What is missing before an alert can be switched on. Each id names a field the agent can fill
// in the client editor. Empty array = the client is specific enough to watch.
//   budget   — no price ceiling
//   location — no district (a map area alone does not reach the leads search → `location_polygon`)
//   size     — no rooms, bedrooms or area (not asked of land / commercial)
// ⚠️ Deal type and property type are NOT gated, on purpose: the client editor cannot change them
// (server v_allowed, core 0039 — they come only from a fresh parsed message), so a gate on them
// would lock the client out with no field to fill. An unknown type simply adds no type filter.
export function leadWatchGaps(criteria, requirement) {
  const c = criteria || {}, r = requirement || {}
  const gaps = []
  const kind = LEAD_WATCH_CATEGORY_KIND[r.property_category] || null
  if (numOf(c.price_max) == null) gaps.push('budget')
  if (!arr(c.district_codes).length && !arr(c.district_must_codes).length) gaps.push(c.search_polygon ? 'location_polygon' : 'location')
  if (!NO_SIZE_KINDS.has(kind) && [c.rooms_min, c.rooms_max, c.bedrooms_min, c.bedrooms_max, c.area_sqm_min, c.area_sqm_max].every(v => numOf(v) == null)) gaps.push('size')
  return gaps
}

const INTENT_KA = { rent: 'ქირა', sale: 'ყიდვა', girao: 'გირავნობა', daily_rent: 'დღიური ქირა' }
const KIND_KA = { apartment: 'ბინა', room: 'ოთახი', house: 'სახლი', land: 'მიწა', commercial: 'კომერციული' }
const fmt = n => Number(n).toLocaleString('en-US')
const range = (lo, hi, unit = '') => {
  const u = unit ? ` ${unit}` : ''
  return lo != null && hi != null ? (lo === hi ? `${fmt(lo)}${u}` : `${fmt(lo)}–${fmt(hi)}${u}`) : hi != null ? `≤ ${fmt(hi)}${u}` : `≥ ${fmt(lo)}${u}`
}

// Compare one lead row (the agent-listings shape) with the client, the way the Desk's classify
// does: a definite miss drops the lead, a missing fact becomes a question for the owner.
//   ctx.districts — [{ code, name_ka, parent_code }] (GET /api/core/districts); without it,
//                   district disagreements become questions, never misses.
//   ctx.featureName(code) → Georgian label (GET /api/core/features).
// Returns { ok, why: string[], verify: string[] }. ok=false: do not announce this lead.
export function explainLeadMatch(criteria, requirement, lead, ctx = {}) {
  const c = criteria || {}, r = requirement || {}, l = lead || {}
  const why = [], verify = [], miss = []
  const fname = code => (ctx.featureName && ctx.featureName(code)) || String(code)
  const districts = Array.isArray(ctx.districts) ? ctx.districts : null
  const dmap = new Map((districts || []).map(d => [d.code, d]))
  const dname = code => (dmap.get(code) && (dmap.get(code).name_ka || dmap.get(code).name_en)) || String(code).replace(/^[A-Z]{3}_/, '').replace(/_/g, ' ')
  const ancestors = code => {
    const out = [], seen = new Set([code])
    let p = dmap.get(code) && dmap.get(code).parent_code
    while (p && !seen.has(p)) { out.push(p); seen.add(p); p = dmap.get(p) && dmap.get(p).parent_code }
    return out
  }

  // deal type
  const wantIntent = LEAD_WATCH_INTENT[r.intent] || null
  if (wantIntent) {
    if (l.intent == null) verify.push('გარიგების ტიპი (ქირა / გაყიდვა)')
    else if (l.intent !== wantIntent) miss.push('intent')
  }
  // property type
  const wantKind = LEAD_WATCH_CATEGORY_KIND[r.property_category] || null
  if (wantKind) {
    if (l.kind == null) verify.push(`ობიექტის ტიპი (სურს: ${KIND_KA[wantKind] || wantKind})`)
    else if (l.kind !== wantKind) miss.push('kind')
  }

  // location
  const want = [...new Set([...arr(c.district_must_codes), ...arr(c.district_codes)])]
  const excluded = new Set(arr(c.excluded_district_codes))
  const codes = [...new Set([l.district_code, l.declared_district_code].filter(present))]
  if (codes.some(code => excluded.has(code) || ancestors(code).some(a => excluded.has(a)))) miss.push('excluded_district')
  else if (want.length) {
    const wantLine = want.slice(0, 3).map(dname).join(', ') + (want.length > 3 ? ` +${want.length - 3}` : '')
    const hit = codes.find(code => want.includes(code) || ancestors(code).some(a => want.includes(a)))
    if (hit) why.push(`📍 ${dname(hit)} (სურს: ${wantLine})`)
    else if (!codes.length) verify.push(`ზუსტი მისამართი / უბანი (სურს: ${wantLine})`)
    // coded only at a parent of the wanted district → could be inside, could be next door
    else if (!districts || codes.some(code => want.some(w => ancestors(w).includes(code)))) verify.push(`ზუსტი უბანი: ${dname(codes[0])} → სურს ${wantLine}`)
    else miss.push('district')
  }

  // price — same currency compares exactly; another currency uses the client's stored
  // conversion (price_max_usd / _gel) and says so, like the Desk's "possible" shelf.
  const lo = numOf(c.price_min), hi = numOf(c.price_max)
  if (lo != null || hi != null) {
    const price = numOf(l.price), cur = l.currency || null
    const budget = range(lo, hi, c.currency || '')
    if (price == null || !cur) verify.push(`ფასი (ბიუჯეტი ${budget})`)
    else {
      const same = !c.currency || c.currency === cur
      const key = cur === 'USD' ? 'usd' : cur === 'GEL' ? 'gel' : null
      const blo = same ? lo : key ? numOf(c[`price_min_${key}`]) : null
      const bhi = same ? hi : key ? numOf(c[`price_max_${key}`]) : null
      if (!same && blo == null && bhi == null) verify.push(`ფასი ${fmt(price)} ${cur} — ბიუჯეტი ${budget}, კურსით შეადარე`)
      else if ((bhi != null && price > bhi) || (blo != null && price < blo)) miss.push('price')
      else if (same) why.push(`💰 ${fmt(price)} ${cur} (ბიუჯეტი ${budget})`)
      else why.push(`💰 ${fmt(price)} ${cur} ≈ ბიუჯეტში (${budget}, კურსით)`)
    }
  }

  // numeric facts the advert may or may not state
  const between = (label, value, min, max, unit, open5 = false) => {
    const a = numOf(min), b0 = numOf(max)
    const b = open5 && b0 != null && b0 >= 5 ? null : b0   // the 5 box is "5+"
    if (a == null && b == null) return
    const v = numOf(value)
    const wantTxt = range(a, b, unit)
    if (v == null) verify.push(`${label} (სურს ${wantTxt})`)
    else if ((a != null && v < a) || (b != null && v > b)) miss.push(label)
    else why.push(`${label}: ${fmt(v)}${unit ? ' ' + unit : ''} (სურს ${wantTxt})`)
  }
  between('ოთახები', l.rooms, c.rooms_min, c.rooms_max, '', true)
  between('საძინებლები', l.bedrooms, c.bedrooms_min, c.bedrooms_max, '', true)
  between('ფართი', l.area_sqm, c.area_sqm_min, c.area_sqm_max, 'მ²')
  between('მიწის ფართი', l.land_area_sqm, c.land_sqm_min, c.land_sqm_max, 'მ²')
  between('სართული', l.floor, c.floor_min, c.floor_max, '')
  const floor = numOf(l.floor), top = numOf(l.floors_total)
  if (c.floor_not_first === true) {
    if (!floor) verify.push('სართული — პირველი არ უნდა იყოს')
    else if (floor === 1) miss.push('floor_not_first')
  }
  if (c.floor_not_last === true) {
    if (floor == null || top == null) verify.push('სართული — ბოლო არ უნდა იყოს')
    else if (floor === top) miss.push('floor_not_last')
  }

  // What only the owner can answer. Nothing in a portal advert settles these, so they are
  // ALWAYS questions — the first one is always whether the place is still free.
  const ask = ['ისევ თავისუფალია? ფასი აქტუალურია?']
  const household = arr(c.household_codes)
  if (household.length) ask.push(`თანახმაა: ${household.map(fname).join(', ')}`)
  if (numOf(c.occupants) != null) ask.push(`იცხოვრებს ${numOf(c.occupants)} ადამიანი`)
  const must = arr(c.must_have_codes)
  if (must.length) ask.push(`აუცილებლად უნდა ჰქონდეს: ${must.map(fname).join(', ')}`)
  const mustNot = arr(c.must_not_have_codes)
  if (mustNot.length) ask.push(`არ უნდა ჰქონდეს: ${mustNot.map(fname).join(', ')}`)
  const cond = arr(c.condition_codes)
  if (cond.length) ask.push(`მდგომარეობა: ${cond.map(fname).join(', ')}`)
  if (present(c.move_in_date)) ask.push(`თავისუფალია ${String(c.move_in_date).slice(0, 10)}-დან?`)
  if (numOf(c.min_lease_months) != null) ask.push(`ვადა მინ. ${numOf(c.min_lease_months)} თვე`)
  if (numOf(c.max_deposit_months) != null) ask.push(`დეპოზიტი ≤ ${numOf(c.max_deposit_months)} თვე`)
  if (numOf(c.prepayment_months) != null) ask.push(`წინასწარ გადახდა: ${numOf(c.prepayment_months)} თვე`)
  if (c.needs_registered_lease === true) ask.push('ხელშეკრულების რეგისტრაცია')
  if (c.mortgage_ready === true) ask.push('იპოთეკით ყიდვა შესაძლებელია?')
  if (c.needs_deeded_parking === true) ask.push('პარკინგი საკუთრებაში')
  if (numOf(c.required_power_kw) != null) ask.push(`სიმძლავრე ≥ ${numOf(c.required_power_kw)} კვტ`)

  return { ok: miss.length === 0, why, verify: [...ask.slice(0, 1), ...verify, ...ask.slice(1)], miss }
}

// The Telegram body. `link` is the Desk URL of the lead.
export function leadMatchMessage(clientName, requirement, lead, explained, link) {
  const r = requirement || {}, l = lead || {}, p = l.property || {}
  const head = [clientName || 'კლიენტი', INTENT_KA[LEAD_WATCH_INTENT[r.intent]], KIND_KA[LEAD_WATCH_CATEGORY_KIND[r.property_category]]].filter(Boolean).join(' · ')
  const where = l.address || l.address_text || p.address_text || ''
  const lines = [`🏠 ახალი ლიდი — ${head}`]
  if (where) lines.push(where)
  if (explained.why.length) lines.push('', '✅ რატომ ემთხვევა:', ...explained.why.map(x => `• ${x}`))
  if (explained.verify.length) lines.push('', '📞 მესაკუთრესთან გადაამოწმეთ:', ...explained.verify.map(x => `• ${x}`))
  if (link) lines.push('', link)
  return lines.join('\n')
}
